<template>
  <div v-if="advert" :class="{ space : isExpandable }" :style="compCssVars">
    <div>
      <p class="text-sm text-gray">Advertisement</p>

      <UtilImage
        :src="compImageSrc"
        class="pm"
        :class="{ square: shape === 'square', 'space-image' : isExpandable }"
        @click="clickAdvert()"
      />
    </div>
  </div>
</template>

<script>
  import {mapState} from 'vuex'
  import advertSpace from '@/lib/mapping/advert-space'
  import UtilImage from '@/components/utils/UtilImage'
  import LegacyApi from '@/lib/api/legacy'

  export default {
  name: 'WidgetAdvert',

  components: {
    UtilImage
  },

  props: {
    // If we have multiple adverts on a page the parent will assign their index
    // We use this to take a unique advert from the available adverts
    advertIndex: {
      type: Number,
      default: 0
    },

    shape: {
      type: String,
      default: 'banner',
      validator: prop => ['banner', 'square', 'leaderboard', 'billboard', 'skyscraper'].includes(prop)
    },

    listingRef: String,

    fetchIsolatedAdvertData: {
      type: Object,
      default: () => {}
    },

    isExpandable: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      advertApi: new LegacyApi(),
      advert: null
    }
  },

  watch: {
    globalAdvertsAvailable: {
      handler() {
        this.getAdvert()
      },
      deep: true
    }
  },

  created() {
    this.getAdvert()
  },

  computed: {
    ...mapState({
      globalAdvertsAvailable: state => state.adverts.available
    }),

    compCssVars() {
      let advertHeight

      if (this.shape === 'banner') {
        advertHeight = this.advert.desktopDimensions.height > 0 ? this.advert.desktopDimensions.height : 90
      }

      if (this.shape === 'leaderboard') {
        advertHeight = this.advert.leaderboardDimensions.height > 0 ? this.advert.leaderboardDimensions.height : 90
      }

      if (this.shape === 'billboard') {
        advertHeight = this.advert.billboardDimensions.height > 0 ? this.advert.billboardDimensions.height : 250
      }

      return {
        '--space-height': `${advertHeight + 21}px`
      }
    },

    compImageSrc() {
      if (!this.advert) {
        return null
      }

      let imageSrc

      if (this.shape === 'banner') {
        if (this.compIsMobileBanner) {
          imageSrc = this.assetBaseUrl.advert + this.advert.mobileFileRef
        } else if (this.advert.desktopFileRef) {
          imageSrc = this.assetBaseUrl.advert + this.advert.desktopFileRef
        }
      }

      if (this.shape === 'square' && this.advert.squareFileRef) {
        imageSrc = this.assetBaseUrl.advert + this.advert.squareFileRef
      }

      if (this.shape === 'leaderboard') {
        imageSrc = this.assetBaseUrl.advert + this.advert.leaderboardFileRef
      }

      if (this.shape === 'billboard') {
        imageSrc = this.assetBaseUrl.advert + this.advert.billboardFileRef
      }

      if (this.shape === 'skyscraper') {
        imageSrc = this.assetBaseUrl.advert + this.advert.skyscraperFileRef
      }

      return imageSrc
    },

    compIsMobileBanner() {
      return this.advert && this.$breakpoint.xs && this.shape === 'banner' && this.advert.mobileFileRef
    }
  },

  methods: {
    async getAdvert() {
      let availableAdverts = { ...this.globalAdvertsAvailable }

      // Don't pool from available adverts, fetch our own
      if (this.fetchIsolatedAdvertData && this.fetchIsolatedAdvertData.commodity) {
        await this.advertApi.post('spaces.get', {
          all: 0,
          ...this.fetchIsolatedAdvertData
        })

        let availableAdverts = {
          banner: [],
          square: [],
          leaderboard: [],
          billboard: [],
          skyscraper: []
        }

        this.advertApi.data.forEach(advert => {
          if (!advert.spaceRef) {
            return
          }

          if (advert.desktopFileRef) {
            availableAdverts.banner.push(advert)
          }

          if (advert.squareFileRef) {
            availableAdverts.square.push(advert)
          }

          if (advert.leaderboardFileRef) {
            availableAdverts.leaderboard.push(advert)
          }

          if (advert.billboardFileRef) {
            availableAdverts.billboard.push(advert)
          }

          if (advert.skyscraperFileRef) {
            availableAdverts.skyscraper.push(advert)
          }
        })
      }

      let suitableAdverts = availableAdverts[this.shape]
      let chosenAdvert = null

      if (suitableAdverts.length > 0) {
        // If we don't have enough adverts for the number the parent is requesting then just show a random one from the right shape
        if (suitableAdverts.length < this.advertIndex + 1) {
          chosenAdvert = suitableAdverts[Math.floor(Math.random() * suitableAdverts.length)]
        } else {
          chosenAdvert = suitableAdverts[this.advertIndex]
        }
      }

      this.advert = chosenAdvert

      if (chosenAdvert) {
        this.$store.dispatch('adverts/logImpression', {
          advert: this.advert,
          listingRef: this.listingRef
        })
      }
    },

    clickAdvert() {
      if (this.advert.spaceRef) {
        let spaceCode = advertSpace.DESKTOP_BANNER

        if (this.compIsMobileBanner) {
          spaceCode = advertSpace.MOBILE_BANNER
        } else if (this.shape === 'square') {
          spaceCode = advertSpace.SQUARE
        } else if (this.shape === 'leaderboard') {
          spaceCode = advertSpace.LEADERBOARD
        } else if (this.shape === 'billboard') {
          spaceCode = advertSpace.BILLBOARD
        } else if (this.shape === 'skyscraper') {
          spaceCode = advertSpace.SKYSCRAPER
        }

        this.$store.dispatch('adverts/logClick', {
          advert: this.advert,
          listingRef: this.listingRef,
          spaceCode,
          linkUrl: this.advert.linkUrl
        })

        // Internal advert? Just redirect locally
        if (this.advert.linkUrl.includes(process.env.VUE_APP_SITE_URL)) {
          let redirectUrl = new URL(this.advert.linkUrl)

          this.$router.push(`${redirectUrl.pathname}${redirectUrl.search}`).catch(() => {})
        } else {
          // Open in app browser if available
          this.$openWindow(this.advert.linkUrl, true)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Use class names that can't be auto detected by adblockers
.pm {
  @apply block w-full cursor-pointer;
  width: 100%;
  max-width: 768px;
}

.pm.square {
  max-width: 300px;
}

.space {
  height: 111px;
  overflow: hidden;
  transition: height 350ms, box-shadow 350ms linear;
  z-index: 5;
}

@media (min-width: 640px) {
  .space {
    &:hover {
      height: var(--space-height);

      .space-image {
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
      }
    }
  }
}
</style>
